import type { FC, ReactNode } from 'react'
import type { ProjectRow } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { formatMonthShort } from '@/lib/dates'
import { getWorkItem, selectedWork } from '@/constants/work'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Reveal, RevealText } from '@/components/generals/reveal'
import { TransitionLink } from '@/components/generals/page-transition'

interface WorkCardProps {
    row: ProjectRow
}

const WorkCard: FC<WorkCardProps> = ({ row }): ReactNode => {
    const image = getWorkItem(row.slug)?.cover ?? row.cover

    return (
        <TransitionLink
            href={`/work?project=${row.slug}`}
            data-cursor='View'
            aria-label={`${row.title}, ${row.category}, ${formatMonthShort(row.date)}`}
            className='group block rounded-[10px] bg-[#131313] p-2.5 transition-colors duration-500 hover:bg-[#171717]'
        >
            <span className='relative block aspect-[16/10] overflow-hidden rounded-[7px] bg-surface'>
                <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    sizes='(min-width: 768px) 48vw, 92vw'
                    className='object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]'
                />
            </span>

            <span className='block px-2.5 pb-3 pt-5 md:px-3 md:pb-4'>
                <span className='flex items-center justify-between gap-4 text-xs text-muted-foreground'>
                    <span>
                        <span aria-hidden='true' className='mr-1.5 text-gold'>/</span>
                        {row.category}
                    </span>

                    <time dateTime={row.date} className='tabular-nums'>
                        {formatMonthShort(row.date)}
                    </time>
                </span>

                <span className='mt-2.5 block text-[clamp(1.25rem,1.8vw,1.65rem)] font-light leading-snug tracking-[-0.025em] text-foreground'>
                    {row.title}
                </span>
            </span>
        </TransitionLink>
    )
}

export const SelectedWork: FC = (): ReactNode => {
    const lastAlone = selectedWork.length % 2 === 1

    return (
        <section id='selected-work' aria-labelledby='selected-work-title' className='px-4 py-28 md:px-[30px] md:py-40'>
            <div>
                <Eyebrow>Selected work</Eyebrow>

                <RevealText
                    id='selected-work-title'
                    as='h2'
                    text='Projects I am proud of'
                    className='type-display mt-6 max-w-[13ch]'
                />
            </div>

            <ul className='mt-14 grid gap-5 md:mt-20 md:grid-cols-2'>
                {selectedWork.map((row, index) => (
                    <li
                        key={row.slug}
                        className={cn(lastAlone && index === selectedWork.length - 1 && 'md:col-span-2 md:mx-auto md:w-[calc(50%_-_0.625rem)]')}
                    >
                        <Reveal y={36} delay={(index % 2) * 0.1}>
                            <WorkCard row={row} />
                        </Reveal>
                    </li>
                ))}
            </ul>
        </section>
    )
}
